import React, { useMemo } from 'react';
import { faFaceSmile, faUsers } from '@fortawesome/pro-regular-svg-icons';

import {
  AGE_BUCKETS,
  GENDER_FEMALE_COLOR,
  GENDER_MALE_COLOR,
  getAgeBucketColor,
} from '../../Analytics/CrowdCountSection/constants';
import {
  FACE, PERSON, fmtTileRC, tileKey,
} from '../helpers';
import style from '../style.module.scss';
import {
  AgeHistogram,
  CONF_COLORS,
  COVERAGE_STOPS,
  ConfidenceChart,
  DistributionPie,
  EmotionsChart,
  FaceAttributesChart,
  LabelsExplorer,
  PersonHierarchyTree,
  Section,
  StatStrip,
  TileHeatmap,
} from './charts';
import {
  ageBucketFromRange,
  buildLabelCatalog,
  collectBoxes,
  countMapEntries,
  densityFromValues,
  parseTileKey,
} from './data';

const DataExplore = ({
  tiles = [],
  tileBoxes,
  rekognitionLabels,
  rows = 1,
  cols = 1,
  showTileCoverage = true,
  onOpenDetail,
}) => {
  const boxes = useMemo(() => collectBoxes(tileBoxes), [tileBoxes]);
  const faces = useMemo(() => boxes.filter((b) => b.label === FACE), [boxes]);
  const persons = useMemo(() => boxes.filter((b) => b.label === PERSON), [boxes]);
  const labelCatalog = useMemo(
    () => buildLabelCatalog(tileBoxes, rekognitionLabels),
    [tileBoxes, rekognitionLabels],
  );

  const tileByCell = useMemo(() => {
    const map = new Map();
    (tiles || []).forEach((tile) => {
      map.set(`${tile.row}-${tile.col}`, tile);
    });
    return map;
  }, [tiles]);

  const openTileCoverage = (row, col, kind) => {
    const tile = tileByCell.get(`${row}-${col}`);
    const key = tile ? tileKey(tile) : null;
    const tileDetections = (key && tileBoxes?.[key]) || [];
    const focusLabel = kind === PERSON ? PERSON : FACE;
    const focused = tileDetections.filter(
      (b) => b.label === focusLabel && b.BoundingBox,
    );
    const count = focused.length;
    const kindLabel = focusLabel === PERSON ? 'persons' : 'faces';
    const faceDetails = focusLabel === FACE
      ? focused.filter((b) => b.raw).map((b) => b.raw)
      : [];
    const labelPayloads = focusLabel === PERSON
      ? (rekognitionLabels || [])
        .filter((obs) => obs.tileKey === key && obs.raw)
        .map((obs) => obs.raw)
      : [];

    onOpenDetail?.({
      kind: 'tile',
      title: `Tile ${fmtTileRC(row, col)}`,
      subtitle: tile
        ? `${count} ${kindLabel}`
        : 'No tile image for this cell',
      tileUrl: tile?.url || null,
      boxes: focused,
      rows: [
        ['Position', fmtTileRC(row, col)],
        ...(tile?.capturedAt
          ? [['Captured', new Date(tile.capturedAt).toLocaleString()]]
          : []),
        [focusLabel === PERSON ? 'Persons' : 'Faces', String(count)],
      ],
      rawLabel: 'AWS Rekognition',
      raw: focusLabel === FACE
        ? { FaceDetails: faceDetails }
        : { Labels: labelPayloads },
    });
  };

  const faceBins = useMemo(() => densityFromValues(faces.map((f) => f.confidence)), [faces]);
  const personBins = useMemo(() => densityFromValues(persons.map((p) => p.confidence)), [persons]);
  const genderConfBins = useMemo(
    () => densityFromValues(faces.map((f) => f.genderConfidence).filter((n) => n != null)),
    [faces],
  );

  const genderSlices = useMemo(() => {
    const map = new Map([['Male', 0], ['Female', 0]]);
    faces.forEach((f) => {
      if (f.gender === 'Male' || f.gender === 'Female') {
        map.set(f.gender, map.get(f.gender) + 1);
      }
    });
    return [
      { name: 'Male', value: map.get('Male'), fill: GENDER_MALE_COLOR },
      { name: 'Female', value: map.get('Female'), fill: GENDER_FEMALE_COLOR },
    ].filter(({ value }) => value > 0);
  }, [faces]);

  const ageBucketSlices = useMemo(() => {
    const map = new Map(AGE_BUCKETS.map((b) => [b, 0]));
    faces.forEach((f) => {
      const bucket = ageBucketFromRange(f.ageLow, f.ageHigh);
      if (!bucket || !map.has(bucket)) return;
      map.set(bucket, map.get(bucket) + 1);
    });
    return AGE_BUCKETS
      .map((bucket, index) => ({
        name: bucket,
        value: map.get(bucket) || 0,
        fill: getAgeBucketColor(bucket, index),
      }))
      .filter(({ value }) => value > 0);
  }, [faces]);

  const ageExactSlices = useMemo(() => {
    const map = new Map();
    faces.forEach((f) => {
      if (!Number.isFinite(f.ageLow) || !Number.isFinite(f.ageHigh)) return;
      const label = `${f.ageLow}-${f.ageHigh}`;
      map.set(label, (map.get(label) || 0) + 1);
    });
    return [...map.entries()]
      .sort(([a], [b]) => {
        const [aL, aH] = a.split('-').map(Number);
        const [bL, bH] = b.split('-').map(Number);
        return aL - bL || aH - bH;
      })
      .map(([name, value]) => ({
        name,
        value,
        fill: '#4E9BF6',
      }));
  }, [faces]);

  const emotionEntries = useMemo(() => {
    const map = new Map();
    faces.forEach((b) => map.set(b.topEmotion || 'Unknown', (map.get(b.topEmotion || 'Unknown') || 0) + 1));
    return countMapEntries(map);
  }, [faces]);


  const faceHeat = useMemo(() => {
    const grid = Array.from({ length: rows }, () => new Array(cols).fill(0));
    Object.entries(tileBoxes || {}).forEach(([key, list]) => {
      const { row, col } = parseTileKey(key);
      if (row < 0 || col < 0 || row >= rows || col >= cols) return;
      grid[row][col] = list.filter((b) => b.label === FACE).length;
    });
    return grid;
  }, [tileBoxes, rows, cols]);

  const personHeat = useMemo(() => {
    const grid = Array.from({ length: rows }, () => new Array(cols).fill(0));
    Object.entries(tileBoxes || {}).forEach(([key, list]) => {
      const { row, col } = parseTileKey(key);
      if (row < 0 || col < 0 || row >= rows || col >= cols) return;
      grid[row][col] = list.filter((b) => b.label === PERSON).length;
    });
    return grid;
  }, [tileBoxes, rows, cols]);

  return (
    <div className={style.explore}>
      <StatStrip
        items={[
          {
            label: 'Persons',
            value: persons.length,
            icon: faUsers,
            tone: 'persons',
          },
          {
            label: 'Faces',
            value: faces.length,
            icon: faFaceSmile,
            tone: 'faces',
          },
        ]}
      />

      <Section title="Gender">
        <DistributionPie
          title="Gender Demographics"
          slices={genderSlices}
        />
      </Section>

      <Section title="Age">
        <div className={style.stack}>
          <AgeHistogram
            title="Age Distribution (Midpoints)"
            rows={ageBucketSlices}
          />
          <AgeHistogram
            title="Age Distribution (Ranges)"
            rows={ageExactSlices}
            printBreak
          />
          <PersonHierarchyTree catalog={labelCatalog} />
        </div>
      </Section>

      <Section title="Confidence" className={style.sectionSpaced}>
        <div className={style.grid3}>
          <ConfidenceChart title="Face" bins={faceBins} color={CONF_COLORS.face} />
          <ConfidenceChart title="Person" bins={personBins} color={CONF_COLORS.person} />
          <ConfidenceChart title="Gender" bins={genderConfBins} color={CONF_COLORS.gender} />
        </div>
      </Section>

      <Section title="Emotions" className="print-break-page">
        <EmotionsChart entries={emotionEntries} />
      </Section>

      <Section title="Face attributes">
        <FaceAttributesChart faces={faces} />
      </Section>

      {showTileCoverage ? (
        <Section title="Per-tile coverage">
          <div className={style.grid2}>
            <TileHeatmap
              title="Faces"
              grid={faceHeat}
              rows={rows}
              cols={cols}
              stops={COVERAGE_STOPS}
              onCellClick={(r, c) => openTileCoverage(r, c, FACE)}
            />
            <TileHeatmap
              title="Persons"
              grid={personHeat}
              rows={rows}
              cols={cols}
              stops={COVERAGE_STOPS}
              onCellClick={(r, c) => openTileCoverage(r, c, PERSON)}
              printBreak
            />
          </div>
        </Section>
      ) : null}

      <Section title="Labels">
        <LabelsExplorer
          tileBoxes={tileBoxes}
          observations={rekognitionLabels}
          onOpenDetail={onOpenDetail}
        />
      </Section>
    </div>
  );
};

export default DataExplore;
